import React, { useEffect, useCallback, PropsWithChildren } from 'react';
import invariant from 'tiny-invariant';
import {
  Box,
  Modal,
  ModalText,
  OHeaderThree,
  OParagraph,
  defaultColors,
  ModalSize,
} from '@lifechurch/react-ion';
import { UrlObject } from 'url';
import { ModalButton } from './styles';
import Router from 'next/router';
import { useTranslation } from 'next-i18next';

type Url = UrlObject | string;
interface BeforeUnloadState {
  shouldConfirmNavigation?: boolean; // when this is false or undefined, it means we needn't do anything
  url?: Url; // when this is set, it's where to go if the user says "Continue"
}

interface BeforeUnloadContextInterface {
  setShouldConfirmNavigation: (shouldConfirmNavigation: boolean) => void;
  maybeRouteTo: (url: Url, isReadOnly?: boolean) => void;
}

const BeforeUnloadContext = React.createContext<BeforeUnloadContextInterface>(
  {} as BeforeUnloadContextInterface
);

export const BeforeUnloadProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation('common');
  const [state, setState] = React.useState<BeforeUnloadState>({});

  const setShouldConfirmNavigation = useCallback((shouldConfirmNavigation: boolean): void => {
    setState(state =>
      shouldConfirmNavigation !== state.shouldConfirmNavigation
        ? { ...state, shouldConfirmNavigation }
        : state
    );
  }, []);

  const maybeRouteTo = useCallback(
    (url: Url, isReadOnly = false): void => {
      if (state.shouldConfirmNavigation && !isReadOnly) {
        setState({ ...state, url });
      } else {
        Router.push(url);
      }
    },
    [state]
  );

  // make sure our state is reset whenever we go to a new page
  useEffect(() => {
    const handleRouteChange = (): void => {
      setState({});
    };
    if (Router.events) {
      // it is undefined under test conditions
      Router.events.on('routeChangeComplete', handleRouteChange);
      return () => {
        Router.events.off('routeChangeComplete', handleRouteChange);
      };
    }
  }, []);

  useEffect(() => {
    if (!state.shouldConfirmNavigation) return;
    function listener(event: BeforeUnloadEvent): string {
      const msg = t('jesus_saves'); // this isn't ever shown, sadly; just have to return any string
      event.preventDefault();
      event.returnValue = msg; // for Gecko & IE
      return msg; // for Webkit etc.
    }
    window.addEventListener('beforeunload', listener);
    return function cleanup() {
      window.removeEventListener('beforeunload', listener);
    };
  }, [state.shouldConfirmNavigation, t]);

  const doCancel = (): void => {
    setState({ ...state, url: undefined });
  };

  const doContinue = (): void => {
    setState({});
    if (state.url) Router.push(state.url);
  };

  const providerValue = {
    setShouldConfirmNavigation,
    maybeRouteTo,
  };

  return (
    <BeforeUnloadContext.Provider value={providerValue}>
      {state.shouldConfirmNavigation && state.url && (
        <Modal dismiss={doCancel} dismissible={true} open={true} size={ModalSize.NARROW}>
          <ModalText>
            <OHeaderThree marginBlockEnd={3}>{t('discard_changes')}</OHeaderThree>
            <OParagraph>{t('unsaved_changes_lost')}</OParagraph>
          </ModalText>
          <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
            <ModalButton color={defaultColors.gray50} onClick={() => doCancel()}>
              {t('cancel')}
            </ModalButton>
            <ModalButton color={defaultColors.error.default} onClick={() => doContinue()}>
              {t('exit_without_saving')}
            </ModalButton>
          </Box>
        </Modal>
      )}
      {children}
    </BeforeUnloadContext.Provider>
  );
};

export const useBeforeUnloadContext = (): BeforeUnloadContextInterface => {
  const context = React.useContext(BeforeUnloadContext);
  invariant(
    context && Object.prototype.hasOwnProperty.call(context, 'maybeRouteTo'),
    'useBeforeUnloadContext must be used within a BeforeUnloadProvider'
  );
  return context;
};
